import type { FC, ReactNode } from 'react'
import type { ProjectSummary } from '@/types'

import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { shapeKey } from '@/lib/format'
import { PointCloud } from '@/components/generals/point-cloud'

interface HoverThumbnailProps {
    project: ProjectSummary | null
}

/**
 * A small point-cloud card that trails the mouse while a project row is hovered.
 * Only used with a mouse on screens 900px and wider; phones, tablets and narrow windows get the
 * inline tap-to-expand preview instead, so the card never covers rows on a small screen.
 */
export const HoverThumbnail: FC<HoverThumbnailProps> = ({ project }): ReactNode => {
    const ref = useRef<HTMLDivElement>(null)
    const target = useRef({ x: 0, y: 0 })
    const position = useRef({ x: 0, y: 0 })
    const [enabled, setEnabled] = useState(false)
    const [mounted, setMounted] = useState(false)
    const [last, setLast] = useState<ProjectSummary | null>(null)

    useEffect(() => {
        const query = window.matchMedia('(hover: hover) and (pointer: fine) and (min-width: 900px)')
        const update = (): void => setEnabled(query.matches)

        update()
        query.addEventListener('change', update)

        return () => query.removeEventListener('change', update)
    }, [])

    useEffect(() => {
        if (!project) return

        setLast(project)
        setMounted(true)
    }, [project])

    useEffect(() => {
        if (!enabled) return

        let raf = 0
        let first = true

        const onMove = (event: PointerEvent): void => {
            target.current = { x: event.clientX, y: event.clientY }

            if (first) {
                first = false
                position.current = { ...target.current }
            }
        }

        const frame = (): void => {
            raf = requestAnimationFrame(frame)

            const element = ref.current

            if (!element) return

            const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
            const ease = reduced ? 1 : 0.16

            position.current.x += (target.current.x - position.current.x) * ease
            position.current.y += (target.current.y - position.current.y) * ease

            const width = element.offsetWidth
            const height = element.offsetHeight
            const flip = position.current.x + width + 40 > window.innerWidth
            const x = flip ? position.current.x - width - 28 : position.current.x + 28
            const y = Math.min(window.innerHeight - height - 16, Math.max(16, position.current.y - height / 2))

            element.style.transform = `translate3d(${x.toFixed(1)}px, ${y.toFixed(1)}px, 0)`
        }

        window.addEventListener('pointermove', onMove, { passive: true })
        raf = requestAnimationFrame(frame)

        return () => {
            cancelAnimationFrame(raf)
            window.removeEventListener('pointermove', onMove)
        }
    }, [enabled])

    if (!enabled || !mounted || !last) return null

    return (
        <div
            ref={ref}
            aria-hidden='true'
            className={cn(
                'pointer-events-none fixed left-0 top-0 z-40 w-[300px] border border-hairline bg-background transition-opacity duration-300 ease-instrument',
                project ? 'opacity-100' : 'opacity-0'
            )}
        >
            <PointCloud
                shape={shapeKey(last.shape, last.slug)}
                label={`Point cloud of ${last.name}`}
                budget='thumb'
                rotate='always'
                rotateSpeed={10}
                effects
                frame
                crosshair={false}
                className='aspect-[4/3] w-full'
            />

            <div className='flex items-center justify-between gap-3 border-t border-hairline px-3 py-2'>
                <span className='hud flex min-w-0 gap-2'>
                    <span className='truncate text-ink' data-mdlfy-cms={`projects:${last.id}:name`}>
                        {last.name}
                    </span>

                    <span className='truncate text-mute' data-mdlfy-cms={`projects:${last.id}:city`}>
                        {last.city}
                    </span>
                </span>

                <span className='size-1.5 shrink-0 bg-signal' />
            </div>
        </div>
    )
}
